import Button from 'components/Button';
import {
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation
} from 'legacy/events/hooks';
import { SectionDto, TierDto } from 'legacy/events/types';
import { useMemo, useState, useEffect, useRef } from 'react';
import { RxDragHandleDots2 } from 'react-icons/rx';
import { BiTrash } from 'react-icons/bi';
import { IoCheckmark, IoChevronDown } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

import * as toast from 'legacy/common/utils/toast';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from '@hello-pangea/dnd';
import classNames from 'classnames';

interface SectionsProps {
  eventId: string;
  tiers?: TierDto[];
  sections?: SectionDto[];
  children: (tickets: TierDto[], sectionId: string) => React.ReactNode;
  onReorder?: (tiers: TierDto[], sections: SectionDto[]) => void;
  onDeleteSection?: (sectionId: string) => void;
}

const Sections = ({
  eventId,
  tiers,
  sections,
  children,
  onReorder,
  onDeleteSection
}: SectionsProps) => {
  const createSectionMutation = useCreateSectionMutation();
  const updateSectionMutation = useUpdateSectionMutation();
  const deleteSectionMutation = useDeleteSectionMutation();

  const [selectedSection, setSelectedSection] = useState<string>();
  const [sectionNames, setSectionNames] = useState<Record<string, string>>({});
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when section is selected
  useEffect(() => {
    if (selectedSection && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedSection]);

  // Watch for successful section creation
  useEffect(() => {
    if (createSectionMutation.isSuccess && sections?.length > 0) {
      // Get the latest section (highest sortIndex)
      const latestSection = sections.reduce((latest, current) =>
        current.sortIndex > latest.sortIndex ? current : latest
      );
      setSelectedSection(latestSection.id);
      handleNameChange(latestSection.id, latestSection.name);

      // Find the scrollable parent container
      setTimeout(() => {
        const scrollableParent = document.querySelector(
          '#modal-scroll-container'
        );
        if (scrollableParent) {
          scrollableParent.scrollTo({
            top: scrollableParent.scrollHeight
          });
        }

        createSectionMutation.reset();
      }, 100);
    }
  }, [createSectionMutation.isSuccess, sections]);

  const tiersBySection: Record<string, TierDto[]> = useMemo(
    () =>
      tiers?.reduce((acc, tier) => {
        acc[tier.sectionId || 'default'] = [
          ...(acc[tier.sectionId || 'default'] || []),
          tier
        ];
        return acc;
      }, {}),
    [tiers]
  );

  const editSection = (sectionId: string, name: string) => {
    setSelectedSection(sectionId);
    setSectionNames((prev) => ({ ...prev, [sectionId]: name }));
  };

  const handleNameChange = (sectionId: string, name: string) => {
    setSectionNames((prev) => ({ ...prev, [sectionId]: name }));
  };

  const handleNameBlur = (sectionId: string) => {
    setSelectedSection(undefined);

    updateSectionMutation.mutate(
      {
        eventId,
        sectionId,
        name: sectionNames[sectionId]
      },
      {
        onSuccess: () => {
          setSectionNames({});
        },
        onError: (error) => {
          toast.showError({
            title: 'Error updating section',
            message: error.message
          });
        }
      }
    );
  };

  const handleDeleteSection = (sectionId: string) => {
    if (deleteSectionMutation.variables?.sectionId === sectionId) {
      return;
    }

    deleteSectionMutation.mutate(
      {
        eventId,
        sectionId
      },
      {
        onSuccess: () => {
          onDeleteSection?.(sectionId);
        },
        onError: (error) => {
          toast.showError({
            title: 'Error deleting section',
            message: error.message
          });
        }
      }
    );
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    // Create base arrays for both sections and tiers
    const currentSections = Array.from(sections || []);
    const currentTiers = Array.from(tiers || []).sort(
      (a, b) => a.sortIndex - b.sortIndex
    );

    if (result.type === 'sections') {
      // Handle section reordering
      const [removedSection] = currentSections.splice(result.source.index, 1);
      currentSections.splice(result.destination.index, 0, removedSection);

      // Update sortIndex for all sections
      const updatedSections = currentSections.map((section, index) => ({
        ...section,
        sortIndex: index
      }));

      onReorder?.(currentTiers, updatedSections);
      return;
    }

    if (result.type === 'tiers') {
      // Handle tier reordering
      const sourceSectionId = result.source.droppableId;
      const destinationSectionId = result.destination.droppableId;
      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;

      // Find the tier being moved
      const sourceSectionTiers = currentTiers.filter(
        (t) => (t.sectionId || 'default') === sourceSectionId
      );
      const tierToMove = sourceSectionTiers[sourceIndex];

      // Remove tier from source section
      const tierIndex = currentTiers.findIndex((t) => t.id === tierToMove.id);
      currentTiers.splice(tierIndex, 1);

      // Add tier to destination section
      const destinationSectionTiers = currentTiers.filter(
        (t) => (t.sectionId || 'default') === destinationSectionId
      );
      const insertAtIndex = Math.min(
        destinationIndex,
        destinationSectionTiers.length
      );

      // Find where to insert in the overall tiers array
      const insertBeforeTier = destinationSectionTiers[insertAtIndex];
      const insertPosition = insertBeforeTier
        ? currentTiers.findIndex((t) => t.id === insertBeforeTier.id)
        : currentTiers.length;

      // Insert the tier at the new position with updated sectionId
      const updatedTier = {
        ...tierToMove,
        sectionId: destinationSectionId
      };
      currentTiers.splice(insertPosition, 0, updatedTier);

      // Update sortIndex for all tiers
      const updatedTiers = currentTiers.map((tier, index) => ({
        ...tier,
        sortIndex: index
      }));

      onReorder?.(updatedTiers, currentSections);
    }
  };

  const sortedSections = useMemo(
    () => sections?.sort((a, b) => a.sortIndex - b.sortIndex),
    [sections]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="sections" type="sections">
        {(providedSections, snapshotSections) => (
          <div
            className={classNames('w-full', {
              'bg-secondary-50 rounded-lg': snapshotSections.isDraggingOver
            })}
            ref={providedSections.innerRef}
            data-rfd-droppable-context-id={
              providedSections.droppableProps['data-rfd-droppable-context-id']
            }
            data-rfd-droppable-id={
              providedSections.droppableProps['data-rfd-droppable-id']
            }
          >
            {/* Default section for tiers without sectionId */}
            {tiersBySection?.default?.length > 0 && (
              <div className="mb-2 px-2">
                {children(tiersBySection?.default || [], 'default')}
              </div>
            )}

            {/* Other sections */}
            {sortedSections?.map((section, index) => (
              <Draggable
                index={index}
                key={section.id}
                draggableId={section.id}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...provided.draggableProps}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...provided.dragHandleProps}
                    className={classNames('relative p-2', {
                      'bg-primary-200 rounded-lg shadow-lg border border-primary-500':
                        snapshot.isDragging
                    })}
                  >
                    <div key={section.id}>
                      <div className="flex items-center">
                        <RxDragHandleDots2
                          size={20}
                          className="cursor-grab text-gray-400 mr-2"
                        />

                        <div className="w-full flex items-center justify-between">
                          {selectedSection === section.id ? (
                            <div className="flex items-center flex-1">
                              <input
                                ref={inputRef}
                                placeholder="eg: New Section"
                                value={sectionNames[section.id] ?? section.name}
                                onChange={(e) =>
                                  handleNameChange(section.id, e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleNameBlur(section.id);
                                  }
                                }}
                                className="text-xl font-medium text-gray-500 border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1"
                              />
                              <div
                                onClick={() => handleNameBlur(section.id)}
                                className="ml-4 p-1 hover:bg-gray-100 rounded-md transition-colors cursor-pointer border border-primary-400"
                                aria-label="Save section name"
                              >
                                <IoCheckmark
                                  size={24}
                                  className="text-primary-500"
                                />
                              </div>
                            </div>
                          ) : (
                            <div
                              className="flex items-center justify-between cursor-pointer bg-gray-100 rounded-md py-4 px-4 flex-1"
                              onClick={() => toggleSection(section.id)}
                            >
                              <p
                                className="text-xl font-medium text-gray-800 text-left"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editSection(section.id, section.name || '');
                                }}
                              >
                                {sectionNames[section.id] ?? section.name}
                              </p>
                              <div>
                                <IoChevronDown
                                  size={24}
                                  className={`ml-2 text-gray-500 transition-transform duration-200 ${
                                    expandedSections[section.id]
                                      ? ''
                                      : 'transform rotate-180'
                                  }`}
                                />
                              </div>
                            </div>
                          )}

                          {section.id !== 'default' && !selectedSection && (
                            <div
                              onClick={() => handleDeleteSection(section.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer ml-4"
                              title="Delete section"
                              aria-label={`Delete ${section.name} section`}
                            >
                              {deleteSectionMutation?.variables?.sectionId ===
                              section.id ? (
                                <AiOutlineLoading3Quarters
                                  size={20}
                                  className="animate-spin"
                                />
                              ) : (
                                <BiTrash size={20} />
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {!expandedSections[section.id] && (
                        <div className="pl-5 border-l border-b border-dashed rounded-bl-md">
                          {children(
                            tiersBySection?.[section.id] || [],
                            section.id
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Draggable>
            ))}

            {providedSections.placeholder}
          </div>
        )}
      </Droppable>

      <div className="flex flex-col items-center justify-center mb-24">
        {createSectionMutation.isLoading && (
          <div className="w-full rounded-lg px-4 animate-pulse mb-10">
            <div className="w-1/2 h-6 bg-gray-200 rounded-md" />
          </div>
        )}

        {createSectionMutation.isLoading ? (
          <AiOutlineLoading3Quarters size={20} className="animate-spin" />
        ) : (
          <Button
            variant="secondary"
            onClick={() => {
              createSectionMutation.mutate({
                eventId,
                name: 'New Section'
              });
            }}
          >
            + Add Section
          </Button>
        )}
      </div>
    </DragDropContext>
  );
};

export default Sections;
